import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Row, Segmented, Tooltip } from 'antd';
import classNames from 'classnames';
import { useContext, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ChannelManager } from 'app/typings/channelManagers';
import type { BulkEditInitialValues } from 'app/typings/core/extranet/rooms/roomBulkEdit';

import type { FieldsDisplayValues } from '../RoomBulkEdit';
import styles from '../RoomBulkEdit.module.scss';

type RoomBulkEditValuesProps = {
  openValues: FieldsDisplayValues;
  initialValues: BulkEditInitialValues;
  toggleSwitch: (
    switchValue: boolean,
    section: keyof FieldsDisplayValues
  ) => void;
};

type ValuesMode = 'discountPrice' | 'discount';

export const RoomBulkEditValues = ({
  openValues,
  initialValues,
  toggleSwitch,
}: RoomBulkEditValuesProps) => {
  const { currencySymbol } = useContext(CurrencyContext);
  const { hotelInfo } = useContext(HotelContext);

  const [valuesMode, setValuesMode] = useState<ValuesMode>('discount');

  return (
    <section className={styles.bulkEditBodyValues}>
      <div className={styles.valuesWrapper}>
        <h2>Select values</h2>
        <div className={styles.valuesContainer}>
          {hotelInfo?.channelManager === ChannelManager.NONE && (
            <>
              <SwitchableInput
                name="stock-switch"
                value={openValues.stock}
                onChange={(value: boolean) => {
                  toggleSwitch(value, 'stock');
                }}
                title={
                  <div className={styles.titleTooltip}>
                    <h3>Stock per day</h3>
                    <Tooltip
                      title="Bookings already made will be deducted from that value"
                      placement="top"
                    >
                      <InfoCircleOutlined color="#BBB" />
                    </Tooltip>
                  </div>
                }
                subtitle={
                  <p className={styles.subtitle}>
                    Number of rooms you want to sell for each day
                  </p>
                }
                childNode={
                  <>
                    <div
                      className={classNames(
                        styles.inputContainer,
                        styles.largeInput
                      )}
                    >
                      <Form.Item
                        shouldUpdate
                        name="stock"
                        initialValue={initialValues.stock}
                        rules={[
                          {
                            required: openValues.stock,
                            message: 'Stock is required',
                          },
                        ]}
                      >
                        <InputNumber
                          precision={0}
                          disabled={!openValues.stock}
                          type="number"
                          min={0}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </div>
                  </>
                }
              />
              <SwitchableInput
                name="price-switch"
                value={openValues.price}
                onChange={(value: boolean) => {
                  toggleSwitch(value, 'price');
                }}
                title={
                  <h3 className={styles.titleTooltip}>Room BAR R/O (NA/NR)</h3>
                }
                subtitle={
                  <p className={styles.subtitle}>
                    Best Available Rate for this room
                  </p>
                }
                childNode={
                  <Row
                    className={classNames(
                      styles.inputContainer,
                      styles.smallInput
                    )}
                  >
                    <Form.Item
                      name="price"
                      rules={[
                        {
                          required: openValues.price,
                          message: 'BAR is required',
                        },
                      ]}
                    >
                      <InputNumber
                        disabled={!openValues.price}
                        min={0}
                        addonAfter={currencySymbol}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </Form.Item>
                  </Row>
                }
              />
            </>
          )}
          <div className={styles.roomDiscountContainer}>
            <div className={styles.modeSelector}>
              <span className={styles.modeLabel}>Select mode:</span>
              <Segmented
                options={['Rate', 'Discount']}
                value={valuesMode === 'discountPrice' ? 'Rate' : 'Discount'}
                onChange={(value) => {
                  setValuesMode(
                    value === 'Rate' ? 'discountPrice' : 'discount'
                  );
                }}
              />
            </div>
            <div className={styles.switchableInputWrapper}>
              <SwitchableInput
                name={
                  <>
                    Room {valuesMode === 'discountPrice' ? 'rate' : valuesMode}{' '}
                    {valuesMode === 'discountPrice' && (
                      <Tooltip title="Staycation rate = BAR + Package price - Staycation discount">
                        <InfoCircleOutlined className={styles.subtitle} />
                      </Tooltip>
                    )}
                  </>
                }
                value={openValues[valuesMode]}
                onChange={(value: boolean) => {
                  toggleSwitch(value, valuesMode);
                }}
                noBackground
                noBorder
                subtitle={
                  valuesMode === 'discountPrice' ? (
                    <p className={styles.subtitle}>
                      Rate of your room only, excluding activities.
                    </p>
                  ) : (
                    <p className={styles.subtitle}>
                      Incentive applied to the room only, excluding activities
                    </p>
                  )
                }
                childNode={
                  valuesMode === 'discountPrice' ? (
                    <Row
                      className={classNames(
                        styles.inputContainer,
                        styles.smallInput
                      )}
                    >
                      <Form.Item
                        name="discountPrice"
                        initialValue={initialValues.discountPrice}
                        rules={[
                          {
                            required: openValues.discountPrice,
                            message: 'Rate is required',
                          },
                        ]}
                      >
                        <InputNumber
                          disabled={!openValues.discountPrice}
                          min={0}
                          addonAfter={currencySymbol}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </Row>
                  ) : (
                    <Row
                      className={`${styles.inputContainer} ${styles.smallInput}`}
                    >
                      <Form.Item
                        name="discount"
                        initialValue={initialValues.discount}
                        rules={[
                          {
                            required: openValues.discount,
                            message: 'Discount is required',
                          },
                        ]}
                      >
                        <InputNumber
                          disabled={!openValues.discount}
                          min={0}
                          max={100}
                          addonAfter="%"
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </Row>
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
